import { ArrayUtils, MGPOptional, Utils } from '@everyboard/lib';
import { Player } from '../../jscaip/Player';
import { PlayerNumberMap } from '../../jscaip/PlayerMap';
import { GameStateWithTable } from '../../jscaip/state/GameStateWithTable';
import { LodestonePieceNone, LodestonePiecePlayer } from './LodestonePiece';
/**
 * Represent different LodestonePressurePlate from the same side of the board
 */
export class LodestonePressurePlateGroup {
    plates;
    static of(sizes) {
        let plates = [];
        for (const size of sizes) {
            const newPlate = new LodestonePressurePlate(size, []);
            plates = plates.concat(newPlate);
        }
        return new LodestonePressurePlateGroup(plates);
    }
    constructor(plates) {
        this.plates = plates;
    }
    getCrumbledPlates() {
        const fullPlates = [];
        for (const plate of this.plates) {
            if (plate.getRemainingSpaces() === 0) {
                fullPlates.push(plate);
            }
            else {
                return fullPlates;
            }
        }
        return fullPlates;
    }
    getCurrentPlate() {
        for (const plate of this.plates) {
            if (plate.getRemainingSpaces() > 0) {
                return MGPOptional.of(plate);
            }
        }
        return MGPOptional.empty();
    }
    getFollowingPlates() {
        const nextPlates = [];
        let currentPlateReached = false;
        for (const plate of this.plates) {
            if (currentPlateReached) {
                nextPlates.push(plate);
            }
            else if (plate.getRemainingSpaces() > 0) {
                currentPlateReached = true;
            }
        }
        return nextPlates;
    }
    getCurrentPlateWidth() {
        const currentPlate = this.getCurrentPlate();
        const emptyplate = new LodestonePressurePlate(0, []);
        return currentPlate.getOrElse(emptyplate).width;
    }
    /**
     * @returns the number of piece that can be put in that pressure plate group, all pressures plates included
     */
    getGroupRemainingSpaces() {
        const remainingSpaces = this.plates.map((plate) => plate.getRemainingSpaces());
        const totalRemainingSpaces = remainingSpaces.reduce((left, right) => left + right);
        return totalRemainingSpaces;
    }
    addCaptured(player, quantity) {
        if (quantity === 0) {
            return this;
        }
        const remainingSpaces = this.getGroupRemainingSpaces();
        Utils.assert(quantity <= remainingSpaces, `should never put more pieces than the plate can support (${remainingSpaces} > ${quantity})`);
        const fullPlates = this.getCrumbledPlates();
        const currentPlate = this.getCurrentPlate().get();
        const nextPlates = this.getFollowingPlates();
        const newPieces = ArrayUtils.copy(currentPlate.getPiecesCopy());
        const maxPiecesToPut = Math.min(quantity, currentPlate.getRemainingSpaces());
        for (let i = 0; i < maxPiecesToPut; i++) {
            newPieces.push(LodestonePiecePlayer.of(player));
            quantity--;
        }
        const newCurrentPlate = new LodestonePressurePlate(currentPlate.width, newPieces);
        const newGroup = new LodestonePressurePlateGroup(fullPlates.concat(newCurrentPlate).concat(nextPlates));
        return newGroup.addCaptured(player, quantity);
    }
    getFillablePlateIndex() {
        let i = 0;
        for (const plate of this.plates) {
            if (plate.getRemainingSpaces() > 0) {
                return i;
            }
            i++;
        }
        return -1;
    }
}
export class LodestonePressurePlate {
    width;
    pieces;
    static POSITIONS = ['top', 'bottom', 'left', 'right'];
    constructor(width, pieces) {
        this.width = width;
        this.pieces = pieces;
    }
    getPieceAt(index) {
        if (index < this.pieces.length) {
            return this.pieces[index];
        }
        else {
            return LodestonePieceNone.EMPTY;
        }
    }
    getRemainingSpaces() {
        return this.width - this.pieces.length;
    }
    getPiecesCopy() {
        return ArrayUtils.copy(this.pieces);
    }
}
// eslint-disable-next-line @typescript-eslint/no-redeclare
export var LodestonePressurePlates;
(function (LodestonePressurePlates) {
    function getInitialLodestonePressurePlates(sizes) {
        const newLodestonePressurePlates = {};
        for (const position of LodestonePressurePlate.POSITIONS) {
            newLodestonePressurePlates[position] = LodestonePressurePlateGroup.of(sizes);
        }
        return newLodestonePressurePlates;
    }
    LodestonePressurePlates.getInitialLodestonePressurePlates = getInitialLodestonePressurePlates;
})(LodestonePressurePlates || (LodestonePressurePlates = {}));
export class LodestoneState extends GameStateWithTable {
    lodestones;
    pressurePlates;
    static SIZE = 8;
    static NUMBER_OF_PIECES = 24;
    static INITIAL_PRESSURE_PLATES = LodestonePressurePlates.getInitialLodestonePressurePlates([5, 3]);
    constructor(board, turn, lodestones, pressurePlates) {
        super(board, turn);
        this.lodestones = lodestones;
        this.pressurePlates = pressurePlates;
    }
    withBoard(board) {
        return new LodestoneState(board, this.turn, this.lodestones, this.pressurePlates);
    }
    remainingSpaces() {
        const remaining = this.remainingSpacesDetails();
        return remaining.top + remaining.bottom + remaining.left + remaining.right;
    }
    remainingSpacesDetails() {
        const remaining = { top: 0, bottom: 0, left: 0, right: 0 };
        for (const position of LodestonePressurePlate.POSITIONS) {
            const pressurePlates = this.pressurePlates[position];
            const currentlyFillablePlateIndex = pressurePlates.getFillablePlateIndex();
            if (currentlyFillablePlateIndex === -1) {
                remaining[position] = 0;
            }
            else {
                remaining[position] = pressurePlates.getGroupRemainingSpaces();
            }
        }
        return remaining;
    }
    numberOfPieces() {
        const playerPieces = PlayerNumberMap.of(0, 0);
        for (let y = 0; y < LodestoneState.SIZE; y++) {
            for (let x = 0; x < LodestoneState.SIZE; x++) {
                const piece = this.getPieceAtXY(x, y);
                if (piece.isPlayerPiece()) {
                    playerPieces.add(piece.owner, 1);
                }
            }
        }
        return playerPieces;
    }
    getScores() {
        const remainingPieces = this.numberOfPieces();
        return PlayerNumberMap.of(LodestoneState.NUMBER_OF_PIECES - remainingPieces.get(Player.ONE), LodestoneState.NUMBER_OF_PIECES - remainingPieces.get(Player.ZERO));
    }
    nextLodestoneDirection() {
        const currentPlayer = this.getCurrentPlayer();
        const lodestonePosition = this.lodestones.get(currentPlayer);
        if (lodestonePosition.isPresent()) {
            const piece = this.getPieceAt(lodestonePosition.get());
            Utils.assert(piece.isLodestone(), 'Piece must be lodestone (invariant from LodestoneState)' + lodestonePosition.get());
            const lodestone = piece;
            const currentDirection = lodestone.direction;
            switch (currentDirection) {
                case 'push': return MGPOptional.of('pull');
                case 'pull': return MGPOptional.of('push');
            }
        }
        else {
            return MGPOptional.empty();
        }
    }
    coordIsOwnedBy(coord, player) {
        const optional = this.getOptionalPieceAt(coord);
        if (optional.isPresent()) {
            return optional.get().owner.equals(player);
        }
        else {
            return false;
        }
    }
}
